import type { ReactNode } from "react";
import React from "react";
import type { TooltipVariant } from "@freightsimple/ui-core/Feedback/Tooltip";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import { QuestionBubbleContext } from "./QuestionBubbleContext";
import {
  QuestionBubbleHeaderBlock,
  QuestionBubbleLabelBlock,
} from "./CustomComponents/Internal/questionBubbleCopy";

/**
 * The bubble glyph itself lives in ui-core now — these are its tooltip triggers.
 * `context` is the dashboard's older name for the same thing, kept because
 * 60-odd call sites pass it.
 *
 * The everyday bubble is the platform's `info` medallion, not the "?" the
 * dashboard used to draw on its own. Two consequences worth knowing: the mark is
 * an "i" rather than a "?", and it's a fixed 20px rather than scaling with the
 * label beside it. MicroWarning's gold became ui-core's orange — gold on a white
 * card was barely there.
 */
const variantForContext: Record<QuestionBubbleContext, TooltipVariant> = {
  [QuestionBubbleContext.Normal]: "info",
  [QuestionBubbleContext.MicroError]: "error",
  [QuestionBubbleContext.MicroWarning]: "warning",
};

interface LearnMoreProps {
  url: string | undefined;
}

const LearnMore_: React.FC<LearnMoreProps> = (props: LearnMoreProps) => {
  function onClick() {
    window.open(props.url, "_new");
  }

  if (props.url === undefined) {
    return <></>;
  } else {
    // A named window, not target="_blank": repeat clicks reuse the same help tab
    // rather than piling up new ones.
    return (
      <T size="xs">
        <Link onClick={onClick}>Learn more</Link>
      </T>
    );
  }
};

export interface QuestionBubbleProps {
  description: string | ReactNode;
  learnMoreUrl?: string | undefined;
  context: QuestionBubbleContext;
}

export const QuestionBubble: React.FC<QuestionBubbleProps> = (
  props: QuestionBubbleProps,
) => {
  return (
    // `roomy` is the tooltip's own "this is an explanation, not a label" mode —
    // it supplies the 320px cap and the comfortable padding this used to
    // hand-roll in a wrapper div.
    <Tooltip
      variant={variantForContext[props.context]}
      side="right"
      roomy
      // Only when there's a "Learn more..." to click. A plain description stays
      // pointer-transparent so the card can't swallow a click underneath it.
      interactive={props.learnMoreUrl !== undefined}
      content={
        <>
          <QuestionBubbleLabelBlock>
            {props.description}
          </QuestionBubbleLabelBlock>
          <LearnMore_ url={props.learnMoreUrl} />
        </>
      }
    />
  );
};

export const QuestionBubbleLabel = QuestionBubbleLabelBlock;
export const QuestionBubbleHeader = QuestionBubbleHeaderBlock;

type ResolvedContextQuestionBubbleType = Omit<QuestionBubbleProps, "context">;
export const NormalQuestionBubble: React.FC<
  ResolvedContextQuestionBubbleType
> = (props: ResolvedContextQuestionBubbleType) => {
  return <QuestionBubble context={QuestionBubbleContext.Normal} {...props} />;
};

interface ToolTipWrapperProps {
  description: ReactNode;
  learnMoreUrl?: string | undefined;
  /**
   * The description holds something to click. Needed because the tooltip card
   * is pointer-transparent by default, which makes a link inside it
   * unreachable. A `learnMoreUrl` implies it.
   */
  interactive?: boolean | undefined;
  children: ReactNode;
}

export function ToolTipWrapper(props: ToolTipWrapperProps) {
  return (
    <Tooltip
      side="right"
      roomy
      interactive={
        props.interactive === true || props.learnMoreUrl !== undefined
      }
      content={
        <>
          <QuestionBubbleLabelBlock>
            {props.description}
          </QuestionBubbleLabelBlock>
          <LearnMore_ url={props.learnMoreUrl} />
        </>
      }
    >
      {props.children}
    </Tooltip>
  );
}
